import type { ReactNode } from 'react';
import styled from 'styled-components';
import { IconNavigation } from '../icons';

const HeaderWrapper = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 ${({ theme }) => theme.layout.headerHeight}px;
  width: 100%;
  height: ${({ theme }) => theme.layout.headerHeight}px;
  min-height: ${({ theme }) => theme.layout.headerHeight}px;
  padding: 0 ${({ theme }) => theme.space[2]}px;
  background: ${({ theme }) => theme.colors.background};
  z-index: ${({ theme }) => theme.zIndex.header};

  ${({ theme }) => theme.device.tablet} {
    padding: 0 ${({ theme }) => theme.space[1]}px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[1]}px;
  min-width: 0;
  flex: 1;
`;

const BrandBlock = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[1]}px;
  min-width: 0;
`;

const LogoSlot = styled.span`
  display: flex;
  flex-shrink: 0;
  align-items: center;
`;

const VerticalRule = styled.span`
  width: 1px;
  height: 18px;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.divider};
`;

/** Не h1: на странице один главный заголовок — у контента (h2). */
const Title = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  line-height: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ theme }) => theme.device.mobile} {
    font-size: ${({ theme }) => theme.fontSizes[0]}px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex-shrink: 0;
`;

const BurgerButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.layout.touchTargetMin}px;
  height: ${({ theme }) => theme.layout.touchTargetMin}px;
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  cursor: pointer;
  transition: background 150ms, color 150ms;

  &:hover {
    background: ${({ theme }) => theme.colors.highlight.gray25};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  ${({ theme }) => theme.device.tablet} {
    display: flex;
  }
`;

export interface HeaderProps {
  title: string;
  logo?: ReactNode;
  actions?: ReactNode;
  onMenuOpen?: () => void;
}

export function Header({ title, logo, actions, onMenuOpen }: HeaderProps) {
  return (
    <HeaderWrapper>
      <HeaderLeft>
        {onMenuOpen && (
          <BurgerButton type="button" onClick={onMenuOpen} aria-label="Открыть меню">
            <IconNavigation />
          </BurgerButton>
        )}
        <BrandBlock>
          {logo && <LogoSlot>{logo}</LogoSlot>}
          {logo && <VerticalRule aria-hidden />}
          <Title>{title}</Title>
        </BrandBlock>
      </HeaderLeft>
      {actions && <HeaderActions>{actions}</HeaderActions>}
    </HeaderWrapper>
  );
}
